import React, { useCallback, useEffect, useState } from 'react';
import '../Download/style.css';

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = Number(bytes);
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

const CATEGORIES = ['일반', '매뉴얼', '정책', '양식', '기타'];

export default function Archive() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: '일반', file: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/archive/files', { credentials: 'include' });
      const data = await res.json();
      setFiles(Array.isArray(data) ? data : (data.files || []));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file || !form.title) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('category', form.category);
      fd.append('file', form.file);
      const res = await fetch('/api/admin/archive', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (data.success) {
        setForm({ title: '', description: '', category: '일반', file: null });
        setOpen(false);
        load();
      } else {
        alert(data.message || '업로드 실패');
      }
    } catch (e) {
      alert('업로드 중 오류: ' + e.message);
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    try {
      const res = await fetch(`/api/admin/archive/${id}`, { method: 'DELETE', credentials: 'include' });
      const data = await res.json();
      if (data.success) load();
      else alert(data.message || '삭제 실패');
    } catch (e) { alert(e.message); }
  };

  return (
    <>
      <header className="admin-page-header">
        <div>
          <h1 className="admin-page-title">자료실 관리</h1>
          <p className="admin-page-desc">자료실(Archive) 파일을 업로드하고 관리합니다. Download 페이지와 동일하게 동작합니다.</p>
        </div>
        <button
          type="button"
          className="admin-action-btn primary"
          style={{ flex: '0 0 auto', minWidth: 110 }}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? '닫기' : '+ 새 업로드'}
        </button>
      </header>

      {open && (
        <form className="ArchiveUploadForm" onSubmit={handleUpload} style={{ marginBottom: 20 }}>
          <div className="ArchiveUploadFormTitle">새 자료 업로드</div>
          <div className="ArchiveUploadGrid">
            <input
              type="text" placeholder="제목 *" required
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              className="ArchiveInput"
            />
            <select
              value={form.category}
              onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
              className="ArchiveInput"
            >
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <input
            type="text" placeholder="설명 (선택)"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            className="ArchiveInput"
          />
          <div className="ArchiveUploadFileRow">
            <label className="ArchiveFileLabel">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              {form.file ? form.file.name : '파일 선택'}
              <input type="file" required style={{ display: 'none' }}
                onChange={(e) => setForm((p) => ({ ...p, file: e.target.files[0] }))}
              />
            </label>
            <div className="ArchiveUploadActions">
              <button type="button" className="ArchiveCancelBtn" onClick={() => { setForm({ title: '', description: '', category: '일반', file: null }); setOpen(false); }}>
                취소
              </button>
              <button type="submit" className="ArchiveUploadBtn" disabled={uploading}>
                {uploading ? '업로드 중...' : '업로드'}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="admin-card" style={{ padding: 0 }}>
        {loading ? (
          <div className="admin-empty">불러오는 중...</div>
        ) : files.length === 0 ? (
          <div className="admin-empty">등록된 자료가 없습니다.</div>
        ) : (
          <div className="ArchiveBoard">
            <div className="ArchiveBoardHead">
              <span className="col-no">번호</span>
              <span className="col-cat">분류</span>
              <span className="col-title">제목</span>
              <span className="col-size">크기</span>
              <span className="col-date">등록일</span>
              <span className="col-action"></span>
            </div>
            {files.map((f, idx) => (
              <div className="ArchiveBoardRow" key={f.id}>
                <span className="col-no">{files.length - idx}</span>
                <span className="col-cat"><span className="ArchiveCatBadge">{f.category}</span></span>
                <div className="col-title">
                  <svg viewBox="0 0 24 24" fill="none" stroke="#01BCD6" strokeWidth="1.8" width="16" height="16" style={{ flexShrink: 0 }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <div>
                    <span className="ArchiveTitleText">{f.title}</span>
                    {f.description && <span className="ArchiveDescText">{f.description}</span>}
                  </div>
                </div>
                <span className="col-size">{formatSize(f.file_size)}</span>
                <span className="col-date">{new Date(f.created_at).toLocaleDateString('ko-KR')}</span>
                <div className="col-action">
                  <a href={`/api/archive/download/${f.id}`} className="ArchiveDownBtn">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    다운로드
                  </a>
                  <button className="ArchiveDelBtn" onClick={() => handleDelete(f.id)}>삭제</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
